import { useState, type FormEvent } from 'react';
import { useRingGroups, useSaveRingGroup, useDeleteTms } from '../../lib/api/hooks-tms.js';
import { useExtensions } from '../../lib/api/hooks.js';
import { TableSkeleton, Empty, ErrorBanner, Badge } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { DestinationPicker, DestinationLabel } from '../../components/telephony/DestinationPicker.js';
import { ApiClientError } from '../../lib/api/client.js';
import type { RingGroup, Destination } from '@arcnet/contracts';

export function RingGroupsTab({ tenantId }: { tenantId: string }) {
  const groups = useRingGroups(tenantId);
  const del = useDeleteTms(tenantId, 'ring-groups');
  const [editing, setEditing] = useState<RingGroup | null>(null);
  const [creating, setCreating] = useState(false);

  return (
    <>
      <div className="arc-row" style={{ marginBottom: 14 }}>
        <div>
          <h2 className="arc-h2">Hunt lists</h2>
          <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            Ring several people for one call, together or in order.
          </p>
        </div>
        <div className="arc-spacer" />
        <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
          onClick={() => { setCreating((v) => !v); setEditing(null); }}>
          <Icon name={creating ? 'close' : 'plus'} size={13} />
          {creating ? 'Cancel' : 'New hunt list'}
        </button>
      </div>

      {groups.error && <ErrorBanner error={groups.error} />}
      {del.error && <ErrorBanner error={del.error} />}
      {(creating || editing) && (
        <RingGroupEditor tenantId={tenantId} existing={editing}
          onDone={() => { setCreating(false); setEditing(null); }} />
      )}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Name</th><th>Dial</th><th>Strategy</th><th>Members</th>
                <th className="arc-td-num">Ring</th><th>On timeout</th><th />
              </tr>
            </thead>
            {groups.isLoading ? <TableSkeleton cols={7} /> : (
              <tbody>
                {groups.data?.data.map((g) => (
                  <tr key={g.id}>
                    <td style={{ fontWeight: 550 }}>{g.name}</td>
                    <td className="arc-mono">{g.extension ?? <span className="arc-subtle">—</span>}</td>
                    <td><Badge tone="muted">{g.strategy.replace(/_/g, ' ')}</Badge></td>
                    <td>
                      <span className="arc-subtle">
                        {g.members.length === 0 ? 'none' :
                          g.members.slice(0, 3).map((m) =>
                            m.extension_number ?? m.external_number).join(', ')}
                        {g.members.length > 3 ? ` +${g.members.length - 3}` : ''}
                      </span>
                    </td>
                    <td className="arc-td-num">{g.ring_time_sec}s</td>
                    <td><DestinationLabel tenantId={tenantId} destination={g.timeout_destination} /></td>
                    <td className="arc-td-right">
                      <div className="arc-row arc-row-actions" style={{ justifyContent: 'flex-end', gap: 2 }}>
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                          onClick={() => { setEditing(g); setCreating(false); }}>Edit</button>
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                          onClick={() => { if (confirm(`Delete hunt list '${g.name}'?`)) del.mutate(g.id); }}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {!groups.isLoading && groups.data?.data.length === 0 && (
          <Empty icon="users" title="No hunt lists"
            hint="A hunt list rings a group of people for one incoming call." />
        )}
      </div>
    </>
  );
}

function RingGroupEditor({ tenantId, existing, onDone }: {
  tenantId: string; existing: RingGroup | null; onDone: () => void;
}) {
  const save = useSaveRingGroup(tenantId);
  const extensions = useExtensions(tenantId, { limit: 200 });

  const [name, setName] = useState(existing?.name ?? '');
  const [ext, setExt] = useState(existing?.extension ?? '');
  const [strategy, setStrategy] = useState(existing?.strategy ?? 'simultaneous');
  const [ringTime, setRingTime] = useState(String(existing?.ring_time_sec ?? 20));
  const [timeout_, setTimeout_] = useState<Destination | null>(existing?.timeout_destination ?? null);
  const [members, setMembers] = useState<string[]>(
    existing?.members.map((m) => m.extension_id).filter((x): x is string => !!x) ?? [],
  );

  const toggle = (id: string) =>
    setMembers((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const err = save.error instanceof ApiClientError ? save.error : null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate({
      ...(existing ? { id: existing.id } : {}),
      name, extension: ext || null, strategy, ring_time_sec: Number(ringTime),
      skip_busy: true,
      timeout_destination: timeout_,
      // Position drives sequential ringing order, so it follows selection order.
      members: members.map((id, i) => ({ extension_id: id, position: i, delay_sec: 0 })),
    }, { onSuccess: onDone });
  };

  return (
    <form className="arc-card arc-card-body" style={{ marginBottom: 14 }} onSubmit={submit}>
      <h3 className="arc-h2" style={{ marginBottom: 12 }}>
        {existing ? `Edit ${existing.name}` : 'New hunt list'}
      </h3>
      {err && (
        <div className="arc-banner arc-banner-danger" style={{ marginBottom: 12 }} role="alert">
          <Icon name="warning" size={14} /><span>{err.message}</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 110px 1fr 100px', gap: 12, marginBottom: 14 }}>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="rg-name">Name</label>
          <input id="rg-name" className="arc-input" required value={name}
            onChange={(e) => setName(e.target.value)} placeholder="Sales team" />
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="rg-ext">Dial code</label>
          <input id="rg-ext" className="arc-input" value={ext}
            onChange={(e) => setExt(e.target.value)} placeholder="200" />
          <div className="arc-hint">optional</div>
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="rg-strat">Ring strategy</label>
          <select id="rg-strat" className="arc-select" value={strategy}
            onChange={(e) => setStrategy(e.target.value as typeof strategy)}>
            <option value="simultaneous">All at once</option>
            <option value="sequential">One after another</option>
            <option value="round_robin">Round robin</option>
            <option value="longest_idle">Longest idle first</option>
            <option value="random">Random</option>
          </select>
        </div>
        <div className="arc-field" style={{ margin: 0 }}>
          <label className="arc-label" htmlFor="rg-time">Ring for</label>
          <input id="rg-time" className="arc-input" inputMode="numeric" value={ringTime}
            onChange={(e) => setRingTime(e.target.value)} />
        </div>
      </div>

      <div className="arc-field">
        <label className="arc-label">Members</label>
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 5,
          border: '1px solid var(--arc-border)', borderRadius: 'var(--arc-r)',
          padding: 9, maxHeight: 150, overflowY: 'auto',
        }}>
          {extensions.data?.data.map((e) => {
            const on = members.includes(e.id);
            return (
              <button
                key={e.id} type="button"
                className={`arc-badge ${on ? 'arc-badge-info' : 'arc-badge-muted'}`}
                style={{ cursor: 'pointer', border: on ? undefined : '1px dashed var(--arc-border-strong)' }}
                onClick={() => toggle(e.id)}
                aria-pressed={on}
              >
                {on && <Icon name="check" size={10} />}
                {e.number} {e.display_name}
              </button>
            );
          })}
        </div>
        <div className="arc-hint">
          {members.length} selected{strategy === 'sequential' ? ' — rung in the order chosen' : ''}
        </div>
      </div>

      <div className="arc-field">
        <label className="arc-label">If nobody answers</label>
        <DestinationPicker tenantId={tenantId} value={timeout_} onChange={setTimeout_} allowNone />
      </div>

      <button type="submit" className="arc-btn arc-btn-primary" disabled={save.isPending}>
        {save.isPending ? 'Saving…' : existing ? 'Save changes' : 'Create hunt list'}
      </button>
    </form>
  );
}
